import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Award, Sparkles, ZoomIn, X, MousePointerClick, Radar } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTrigger, DialogClose } from '../ui/dialog';
import FreeTrialModal from '../modals/FreeTrialModal';
import { Reveal, AnimatedGradientBg, CountUp } from '../pop/motion';
import HeroQuotes from './HeroQuotes';

/**
 * Infographic hotspots — clickable overlays that navigate CXOs from the
 * lightbox directly into the relevant assessment / product page.
 *
 * Coordinates are calibrated to the PRECISE tile bounding boxes of the
 * source PNG (1397×1126), detected via NumPy content-analysis of the
 * FEATURES and USES quadrants:
 *
 *   Tile column centers   (X%): 7.85 · 19.29 · 30.75
 *   FEATURES row centers  (Y%): 15.63 · 29.22 · 42.06
 *   USES     row centers  (Y%): 54.13 · 66.57
 *
 * Each hotspot has a small inset (~0.5-1%) inside the tile so the emerald
 * hover ring hugs the tile perimeter without spilling into neighbours —
 * a clean, "hairline-inset" polish.
 */
const INFOGRAPHIC_HOTSPOTS = [
  // ---- FEATURES quadrant (top-left, 3×3 grid) ----
  { label: 'Organizational Performance', to: '/performance-insights', left: '2.5%',  top: '9.5%',   width: '10.5%', height: '12.4%' },
  { label: 'Data',                       to: '/data-maturity',        left: '14.0%', top: '9.5%',   width: '10.5%', height: '12.4%' },
  { label: 'Digital',                    to: '/digital-maturity',     left: '25.5%', top: '9.5%',   width: '10.5%', height: '12.4%' },
  { label: 'AI',                         to: '/ai-maturity',          left: '2.5%',  top: '23.0%',  width: '10.5%', height: '12.4%' },
  { label: 'BPCM',                       to: '/bpcm-assessment',      left: '14.0%', top: '23.0%',  width: '10.5%', height: '12.4%' },
  { label: 'Sustainability',             to: '/sustainability',       left: '25.5%', top: '23.0%',  width: '10.5%', height: '12.4%' },
  { label: 'Cyber Security',             to: '/cyber-security',       left: '2.5%',  top: '36.5%',  width: '10.5%', height: '11.0%' },
  { label: 'Executive Dashboards',       to: '/dashboard',            left: '14.0%', top: '36.5%',  width: '10.5%', height: '11.0%' },
  { label: 'Agentic AI',                 to: '/multi-agent-dashboard',left: '25.5%', top: '36.5%',  width: '10.5%', height: '11.0%' },
  // ---- USES quadrant (bottom-left, 2×3 grid) ----
  { label: 'Maturity Assessments',       to: '/kpi-scorecard',         left: '2.5%',  top: '48.5%',  width: '10.5%', height: '11.3%' },
  { label: 'Performance Intelligence',   to: '/performance-insights',  left: '14.0%', top: '48.5%',  width: '10.5%', height: '11.3%' },
  { label: 'Strategic Decision Support', to: '/multi-agent-dashboard', left: '25.5%', top: '48.5%',  width: '10.5%', height: '11.3%' },
  { label: 'Benchmarking',               to: '/benchmark-intelligence',left: '2.5%',  top: '60.9%',  width: '10.5%', height: '11.3%' },
  { label: 'Transformation Roadmaps',    to: '/impact-intelligence',   left: '14.0%', top: '60.9%',  width: '10.5%', height: '11.3%' },
  { label: 'Advisory & Academy',         to: '/pop/ai-mba',            left: '25.5%', top: '60.9%',  width: '10.5%', height: '11.3%' },
];

/**
 * CXO-tier hero — designed to sit alongside McKinsey / BCG / Bain
 * without altering any functionality: primary CTA still opens the
 * FreeTrialModal, the PoP sub-portal link still routes to /pop,
 * and the SAMANVAI overview infographic anchors the right column.
 */
const HeroSection = () => {
  const [showTrialModal, setShowTrialModal] = useState(false);

  return (
    <>
      <FreeTrialModal isOpen={showTrialModal} onClose={() => setShowTrialModal(false)} />

      <section className="relative overflow-hidden pt-16 sm:pt-17 lg:pt-[72px]" data-testid="hero-section">
        {/* -------- Editorial navy background -------- */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(20,83,45,0.10),transparent_55%),radial-gradient(ellipse_at_top_right,rgba(14,116,144,0.10),transparent_55%),linear-gradient(180deg,#0a1220_0%,#0a1220_45%,#0f1729_100%)]" />
          {/* Subtle grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.045]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
              backgroundSize: '64px 64px',
              maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 78%)',
            }}
          />
          {/* Ultra-slow drifting aurora — subtle, McKinsey-quiet */}
          <AnimatedGradientBg className="opacity-40" />
          {/* Editorial vignette at the bottom to blend into the next section */}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-slate-950/60" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-14 sm:pt-6 sm:pb-20 lg:pt-7 lg:pb-24">
          {/* -------- Editorial masthead kicker -------- */}
          <Reveal delay={0} className="mb-5">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] font-bold uppercase text-emerald-300">
                <span className="w-2 h-2 bg-emerald-400 rounded-full" />
                Powered by Agentic AI
              </div>
              <div className="h-3 w-px bg-white/15" />
              <div className="text-[10px] tracking-[0.32em] font-bold uppercase text-white/60">
                For CXOs &nbsp;·&nbsp; Boards &nbsp;·&nbsp; Transformation Offices
              </div>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-x-12 gap-y-10 items-start">
            {/* ================================================ LEFT ================================================ */}
            <div className="max-w-2xl">
              <Reveal delay={80}>
                <h1 className="font-serif text-white tracking-tight text-[26px] sm:text-[32px] lg:text-[40px] leading-[1.08] mb-4">
                  The performance intelligence platform{' '}
                  <span className="italic text-emerald-300">CXOs trust</span>{' '}
                  to unlock enterprise value.
                </h1>
              </Reveal>

              <Reveal delay={200}>
                <p className="text-[15px] sm:text-base text-white/70 leading-relaxed max-w-xl mb-6 font-light">
                  SAMANVAI benchmarks your organization against industry leaders and top performers —
                  turning diagnostic depth into <span className="text-white font-normal">board-ready action, in minutes</span>.
                </p>
              </Reveal>

              <Reveal delay={280}>
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* -------- LEFT: Explore Company Assessment -------- */}
                  <div
                    data-testid="hero-assessment-panel"
                    className="flex flex-col rounded-2xl bg-white/[0.04] ring-1 ring-white/12 hover:ring-white/25 transition-colors p-5"
                  >
                    <div className="text-[9.5px] tracking-[0.26em] font-bold uppercase text-emerald-300 mb-2.5">
                      Self-serve maturity diagnostic
                    </div>
                    <p className="text-[13px] text-white/75 leading-relaxed mb-5">
                      KPI scorecards, peer benchmarks, a 5-year roadmap and AGI insights — see your{' '}
                      <span className="text-white font-medium">sample report instantly</span>; the full report is
                      shared personally by the SAMANVAI team.
                    </p>
                    <Button
                      size="lg"
                      className="mt-auto w-full bg-white text-slate-950 hover:bg-slate-100 h-11 rounded-full text-sm font-semibold shadow-lg shadow-black/20 group"
                      onClick={() => setShowTrialModal(true)}
                      data-testid="explore-company-assessment-btn"
                    >
                      Explore Company Assessment
                      <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                  </div>

                  {/* -------- RIGHT: Company Intelligence -------- */}
                  <div
                    data-testid="hero-ci-panel"
                    className="flex flex-col rounded-2xl bg-teal-400/[0.05] ring-1 ring-teal-400/30 hover:ring-teal-300/60 transition-colors p-5"
                  >
                    <div className="flex items-center gap-2 text-[9.5px] tracking-[0.26em] font-bold uppercase text-teal-300 mb-2.5">
                      15-agent board intelligence
                      <span className="text-[8.5px] tracking-[0.14em] text-amber-300 border border-amber-300/50 rounded-full px-1.5 py-px">NEW</span>
                    </div>
                    <p className="text-[13px] text-white/75 leading-relaxed mb-5">
                      15 AI agents turn annual reports and filings into board-grade reports{' '}
                      <span className="text-white font-medium">in minutes</span> — board reviews, investment
                      analysis, M&amp;A diligence — delivered in collaboration with the SAMANVAI team.
                    </p>
                    <Link
                      to="/company-intelligence"
                      data-testid="nav-company-intelligence-btn"
                      className="mt-auto inline-flex items-center justify-center gap-2 w-full h-11 rounded-full border border-teal-400/60 bg-teal-400/[0.08] hover:border-teal-300 hover:bg-teal-400/[0.16] text-sm font-bold text-teal-300 transition-colors group"
                    >
                      <Radar className="w-4 h-4" />
                      Company Intelligence
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  </div>
                </div>

                <a
                  href="#executive-summary"
                  data-testid="hero-read-pov-link"
                  className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-white/70 hover:text-white group border-b border-transparent hover:border-white/40 transition-colors"
                >
                  Read our point of view
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </Reveal>

              {/* -------- Board-tier proof rail -------- */}
              <Reveal delay={380}>
                <div className="mt-14 grid grid-cols-3 divide-x divide-white/10 border-t border-white/10 pt-7">
                  {[
                    { v: 500, suffix: '+', l: 'Enterprise clients', sub: 'Fortune 500 & mid-market' },
                    { v: 128, suffix: '',  l: 'Countries served',    sub: 'Across 14 sectors' },
                    { v: 25,  suffix: '+', l: 'Years of practice',   sub: 'CXO advisory heritage' },
                  ].map((s, i) => (
                    <div key={s.l} className={`px-6 ${i === 0 ? '!pl-0' : ''}`}>
                      <div className="font-serif text-white text-4xl sm:text-5xl leading-none tabular-nums">
                        <CountUp value={s.v} suffix={s.suffix} />
                      </div>
                      <div className="mt-2 text-[11px] tracking-[0.18em] uppercase font-bold text-white/85">{s.l}</div>
                      <div className="text-[11px] text-white/45 mt-0.5">{s.sub}</div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* ================================================ RIGHT ================================================ */}
            <Reveal delay={450} className="relative">
              {/* Editorial glow frame — highlights the infographic without competing */}
              <div className="hidden lg:block absolute -inset-6 rounded-[32px] bg-gradient-to-br from-emerald-500/[0.14] via-cyan-500/[0.06] to-amber-400/[0.10] blur-3xl" />
              <div className="hidden lg:block absolute -inset-2 rounded-3xl bg-gradient-to-tr from-transparent via-white/[0.02] to-white/[0.06]" />

              <div className="relative">
                {/* Rotating client quotes */}
                <HeroQuotes />

                {/* Seven standalone diagnostic lenses */}
                <div className="mt-7" data-testid="hero-lenses">
                  <div className="font-serif text-white text-lg sm:text-xl mb-5">
                    Seven standalone diagnostic lenses.{' '}
                    <span className="italic text-emerald-300">One board-ready view.</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                    {[
                      { k: 'Data-driven excellence', n: 'Data Maturity', to: '/data-maturity' },
                      { k: 'Digital transformation', n: 'Digital Maturity', to: '/digital-maturity' },
                      { k: 'AI readiness', n: 'AI Maturity', to: '/ai-maturity' },
                      { k: 'ESG excellence', n: 'Sustainability / ESG', to: '/sustainability' },
                      { k: 'Change management', n: 'BPCM Assessment', to: '/bpcm-assessment' },
                      { k: 'Security posture', n: 'Cyber Security', to: '/cyber-security' },
                      { k: 'Supply-chain performance', n: 'SCOR Diagnostic', to: '/diagnostic' },
                    ].map((l) => (
                      <Link
                        key={l.to}
                        to={l.to}
                        data-testid={`hero-lens-${l.to.replace('/', '')}`}
                        className="group block border-t border-white/15 pt-2.5 hover:border-emerald-300/70 transition-colors"
                      >
                        <div className="text-[9px] tracking-[0.22em] uppercase font-bold text-white/45 group-hover:text-emerald-300/80 transition-colors mb-1">
                          {l.k}
                        </div>
                        <div className="flex items-center gap-1.5 text-[14px] font-semibold text-white/85 group-hover:text-white transition-colors leading-snug">
                          {l.n}
                          <ArrowRight className="w-3.5 h-3.5 text-white/30 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all shrink-0" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* ================================================ SAMANVAI OPERATING SYSTEM — bottom ================================================ */}
          <Reveal delay={120}>
            <div className="mt-16 sm:mt-20 max-w-4xl mx-auto">
              <div className="flex flex-wrap items-center justify-center gap-2 mb-4 text-[10px] tracking-[0.3em] font-bold uppercase text-white/60">
                <Sparkles className="w-3 h-3 text-emerald-300" />
                The SAMANVAI Operating System
                <span className="text-white/25 mx-1">·</span>
                Features · Process · Uses · Impact
              </div>
              <Dialog>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      aria-label="Open the SAMANVAI operating-system diagram at full size"
                      data-testid="hero-infographic-trigger"
                      className="group relative block w-full rounded-2xl ring-1 ring-white/15 shadow-2xl shadow-black/50 bg-white overflow-hidden transition-transform duration-500 ease-out hover:-translate-y-1 hover:ring-emerald-300/40 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 hero-infographic-frame"
                    >
                      {/* Corner sheen */}
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 mix-blend-overlay transition-opacity duration-700" />
                      <img
                        src="/samanvai-infographic.png"
                        alt="SAMANVAI — AI-powered organisational performance operating system: Features, Process, Uses, Benefits and Measurable Impact"
                        loading="eager"
                        fetchPriority="high"
                        className="block w-full h-auto object-contain select-none hero-infographic-image"
                        data-testid="hero-infographic"
                      />
                      {/* "Click to zoom" affordance — visible on hover */}
                      <div className="pointer-events-none absolute top-3 right-3 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                        <div className="flex items-center gap-1.5 rounded-full bg-slate-900/85 backdrop-blur-md text-white text-[10px] font-semibold tracking-[0.18em] uppercase pl-2.5 pr-3 py-1.5 ring-1 ring-white/20 shadow-lg">
                          <ZoomIn className="w-3 h-3 text-emerald-300" />
                          Click to zoom
                        </div>
                      </div>
                    </button>
                  </DialogTrigger>

                  {/* Full-viewport lightbox — no default chrome, we render our own */}
                  <DialogContent
                    className="w-[96vw] max-w-[1600px] p-0 border-0 bg-transparent shadow-none rounded-none [&>button.absolute]:hidden"
                    data-testid="hero-infographic-lightbox"
                  >
                    <div className="relative flex items-center justify-center">
                      {/* Image + hotspot layer share the same relative container so % coords stay perfectly aligned */}
                      <div
                        className="relative rounded-lg shadow-2xl bg-white overflow-hidden"
                        style={{ aspectRatio: '1397 / 1126', width: '100%', maxHeight: '92vh', maxWidth: 'calc(92vh * (1397 / 1126))' }}
                      >
                        <img
                          src="/samanvai-infographic.png"
                          alt="SAMANVAI operating system — enlarged view"
                          className="absolute inset-0 w-full h-full object-contain select-none"
                          data-testid="hero-infographic-lightbox-image"
                        />
                        {/* Clickable hotspots — one per infographic label */}
                        {INFOGRAPHIC_HOTSPOTS.map((h) => (
                          <DialogClose asChild key={h.label}>
                            <Link
                              to={h.to}
                              data-testid={`hero-hotspot-${h.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                              aria-label={`Explore ${h.label}`}
                              className="group/hs absolute rounded-md ring-1 ring-transparent hover:ring-emerald-400/80 hover:bg-emerald-400/[0.10] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 transition-all duration-200"
                              style={{ left: h.left, top: h.top, width: h.width, height: h.height }}
                            >
                              {/* Tiny corner arrow appears on hover — non-intrusive */}
                              <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white opacity-0 group-hover/hs:opacity-100 group-focus-visible/hs:opacity-100 shadow-md transform-gpu scale-90 group-hover/hs:scale-100 transition-all duration-200">
                                <ArrowRight className="w-3 h-3" />
                              </span>
                              {/* Route pill below on hover */}
                              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-slate-900/95 text-white text-[9px] font-semibold tracking-[0.18em] uppercase px-2.5 py-1 opacity-0 group-hover/hs:opacity-100 group-focus-visible/hs:opacity-100 pointer-events-none transition-opacity duration-200 shadow-lg ring-1 ring-white/15">
                                Explore {h.label}
                              </span>
                            </Link>
                          </DialogClose>
                        ))}
                      </div>

                      {/* Editorial close button — always visible, dark pill for contrast */}
                      <DialogClose asChild>
                        <button
                          data-testid="hero-infographic-lightbox-close"
                          aria-label="Close enlarged infographic"
                          className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-slate-900/90 backdrop-blur-md text-white text-[11px] font-semibold tracking-[0.18em] uppercase pl-3 pr-3.5 py-2 ring-1 ring-white/20 shadow-xl hover:bg-slate-900 hover:ring-emerald-300/60 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
                        >
                          <X className="w-3.5 h-3.5" />
                          Close
                        </button>
                      </DialogClose>

                      {/* Editorial hint bar — tells CXOs the image is interactive */}
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-slate-900/90 backdrop-blur-md text-white text-[10px] font-semibold tracking-[0.22em] uppercase pl-2.5 pr-3 py-1.5 ring-1 ring-white/20 shadow-xl pointer-events-none">
                        <MousePointerClick className="w-3 h-3 text-emerald-300" />
                        Click any tile to jump to the assessment
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <div className="flex flex-wrap justify-center items-center gap-2 mt-3 text-[11px] text-white/55">
                  <TrendingUp className="w-3 h-3 text-emerald-300" />
                  <span className="italic">
                    Efficiency <span className="text-white/80 font-medium">+25-30%</span>
                    <span className="text-white/25 mx-1.5">·</span>
                    Productivity <span className="text-white/80 font-medium">+35-40%</span>
                    <span className="text-white/25 mx-1.5">·</span>
                    Sustainability <span className="text-white/80 font-medium">+45-50%</span>
                    <span className="text-white/25 mx-1.5">·</span>
                    Profitability <span className="text-white/80 font-medium">+15-20%</span>
                  </span>
                </div>
              </div>

              <style>{`
                @keyframes heroInfographicReveal {
                  0%   { opacity: 0; transform: translateY(14px) scale(0.985); filter: blur(6px); }
                  60%  { opacity: 1; filter: blur(0); }
                  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
                }
                .hero-infographic-frame {
                  animation: heroInfographicReveal 1.1s cubic-bezier(.2,.7,.2,1) 0.15s both;
                }
                .hero-infographic-image {
                  transition: transform 8s ease-out;
                  transform-origin: center;
                }
                .group:hover .hero-infographic-image {
                  transform: scale(1.03);
                }
              `}</style>
            </Reveal>

          {/* ================================================ EDITORIAL FOOTPRINT STRIP ================================================ */}
          <Reveal delay={520}>
            <div className="mt-16 sm:mt-20 pt-8 border-t border-white/10">
              <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-5">
                <Award className="w-3 h-3 text-amber-300" />
                Trusted across boardrooms, transformation offices &amp; regulators
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-4 items-center">
                {[
                  'FORTUNE 500 CXOs',
                  'BFSI TRANSFORMATION',
                  'MANUFACTURING 4.0',
                  'PUBLIC SECTOR',
                  'BIG-4 ADVISORY',
                  'UGC-ALIGNED',
                ].map((label) => (
                  <div
                    key={label}
                    className="font-serif italic text-white/40 hover:text-white/70 transition-colors text-sm tracking-wide text-center border-l border-white/10 first:border-l-0 pl-8 first:pl-0 sm:pl-0 sm:border-none"
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
